import type { SpecType } from '@local/lib';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { ICustomTemplateCreate, ICustomTemplateUpdate, QualityAdjustmentTemplate } from '../templateTypes';

import { fetchCustomTemplateById } from '../../../api/templatesApi';
import {
  customTemplateQueryOptions,
  customTemplatesListQueryOptions,
  useCreateCustomTemplate,
  useDeleteCustomTemplate,
  useUpdateCustomTemplate,
} from './useCustomTemplates';

// Non-suspending — both the Template Manager list and Contract Entry's "Load Template" dropdown
// want to render immediately and show their own loading/empty states rather than suspend the
// whole page. Backed by the same API-backed query options useCustomTemplates.ts exposes — see
// that file for the actual create/update/delete/list/detail data layer — just consumed with
// `useQuery` instead of `useSuspenseQuery`, and projected down to a bare array via `select` so
// this hook's shape never has to change for callers.
//
// Deliberately NOT passed as a global-search query (`isGlobalSearch: true`) — that mode disables
// `refetchOnMount`, which would leave this, the primary "all templates" list, showing stale data
// after navigating back from a save until a manual page refresh. Normal list semantics (refetch
// on mount, 2-minute staleTime) are what this list actually needs.
export const useTemplates = () =>
  useQuery({
    ...customTemplatesListQueryOptions('*', {}),
    select: response => response.items,
  });

export const useTemplate = (id: null | string) =>
  useQuery({
    ...customTemplateQueryOptions('*', id ?? ''),
    enabled: id !== null,
    // The Template Editor seeds its form fields from this data exactly once, at mount — it never
    // re-reads a later cache update — so opening an edit page must always hit the network for the
    // current value instead of trusting whatever's already cached (e.g. from a prior visit).
    refetchOnMount: 'always',
  });

// Every template belongs to exactly one Quality Specification (see templateTypes.ts), so
// "templates for Moisture" vs. "templates for HGI" is purely a filter over the same list — no
// per-spec branch here, which is what lets a brand-new SpecType show its own scoped templates with
// zero changes to this hook. Reuses the same `templates.all()` cache `useTemplates` populates
// (React Query dedupes identical query keys) — this is the hook Contract Entry's (and the
// Template Editor's) per-spec "Load Template" control calls.
export const useTemplatesForSpec = (specType: SpecType) => {
  const { data, isLoading } = useTemplates();

  const templates = useMemo(() => (data ?? []).filter(template => template.specType === specType), [data, specType]);

  return { isLoading, templates };
};

// Thin adapter over useCustomTemplates.ts's create/update/delete mutation hooks, kept so every
// existing call site (TemplateManagerPage, TemplateEditorPage) can keep its original "try/catch +
// notify" shape instead of wiring up three separate `useMutation` results itself.
export const useTemplateActions = () => {
  const createMutation = useCreateCustomTemplate();
  const updateMutation = useUpdateCustomTemplate();
  const deleteMutation = useDeleteCustomTemplate();

  return {
    createTemplate: (input: ICustomTemplateCreate): Promise<QualityAdjustmentTemplate> =>
      createMutation.mutateAsync({ data: input, scope: input.specType }),

    duplicateTemplate: async (id: string): Promise<QualityAdjustmentTemplate> => {
      const source = await fetchCustomTemplateById('*', id);
      const { config, description, name, specType } = source;
      return createMutation.mutateAsync({
        data: { config, description, name: `${name} (Copy)`, specType },
        scope: specType,
      });
    },

    removeTemplate: (id: string): Promise<void> => deleteMutation.mutateAsync({ scope: '*', templateId: id }),

    updateTemplate: (id: string, input: ICustomTemplateUpdate): Promise<QualityAdjustmentTemplate> =>
      updateMutation.mutateAsync({ data: input, scope: input.specType, templateId: id }),
  };
};
